/**
 * @fileoverview ClubOS Role & Permission Management page
 * Shows all roles with permission matrix for RBAC overview.
 * TODO: Replace static data with API: userService.getRoles(), userService.getPermissions()
 */

import { useState } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import Divider from "@mui/material/Divider";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import Alert from "@mui/material/Alert";

// ClubOS components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftBadge from "components/SoftBadge";
import SoftButton from "components/SoftButton";

// ClubOS examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// ClubOS RBAC data
import { ROLES, PERMISSIONS, ROLE_PERMISSIONS, ROLE_META } from "constants/roles";
import { MOCK_USERS } from "data/userData";

// ─── CONSTANTS ─────────────────────────────────────────────────────────────────

/** Human-readable permission labels */
const PERMISSION_LABELS = {
  [PERMISSIONS.FINANCE_VIEW]:      "View Finance",
  [PERMISSIONS.FINANCE_EDIT]:      "Edit Finance Records",
  [PERMISSIONS.FINANCE_ADMIN]:     "Finance Administration",
  [PERMISSIONS.SALARY_VIEW]:       "View Salaries (Sensitive)",
  [PERMISSIONS.SALARY_EDIT]:       "Edit Salaries",
  [PERMISSIONS.TRANSFER_VIEW]:     "View Transfers",
  [PERMISSIONS.TRANSFER_EDIT]:     "Edit Transfers",
  [PERMISSIONS.TRANSFER_APPROVE]:  "Approve Transfers",
  [PERMISSIONS.BUDGET_VIEW]:       "View Budget",
  [PERMISSIONS.BUDGET_EDIT]:       "Edit Budget",
  [PERMISSIONS.REVENUE_VIEW]:      "View Revenue",
  [PERMISSIONS.REVENUE_EDIT]:      "Edit Revenue",
  [PERMISSIONS.SQUAD_VIEW]:        "View Squad",
  [PERMISSIONS.SQUAD_EDIT]:        "Edit Squad",
  [PERMISSIONS.SQUAD_ADMIN]:       "Squad Administration",
  [PERMISSIONS.COACHING_VIEW]:     "View Coaching",
  [PERMISSIONS.COACHING_EDIT]:     "Edit Coaching Plans",
  [PERMISSIONS.COACHING_ADMIN]:    "Coaching Administration",
  [PERMISSIONS.SCOUTING_VIEW]:     "View Scouting",
  [PERMISSIONS.SCOUTING_EDIT]:     "Edit Scouting Reports",
  [PERMISSIONS.SCOUTING_ADMIN]:    "Scouting Administration",
  [PERMISSIONS.USERS_VIEW]:        "View Users",
  [PERMISSIONS.USERS_EDIT]:        "Edit Users",
  [PERMISSIONS.USERS_ADMIN]:       "User Administration",
  [PERMISSIONS.REPORTS_VIEW]:      "View Reports",
  [PERMISSIONS.REPORTS_EXPORT]:    "Export Reports",
};

/** Permission groups for the matrix display */
const PERMISSION_GROUPS = [
  {
    label: "Finance",
    permissions: [
      PERMISSIONS.FINANCE_VIEW, PERMISSIONS.FINANCE_EDIT, PERMISSIONS.FINANCE_ADMIN,
      PERMISSIONS.SALARY_VIEW, PERMISSIONS.SALARY_EDIT,
      PERMISSIONS.TRANSFER_VIEW, PERMISSIONS.TRANSFER_EDIT, PERMISSIONS.TRANSFER_APPROVE,
      PERMISSIONS.BUDGET_VIEW, PERMISSIONS.BUDGET_EDIT,
      PERMISSIONS.REVENUE_VIEW, PERMISSIONS.REVENUE_EDIT,
    ],
  },
  {
    label: "Squad",
    permissions: [PERMISSIONS.SQUAD_VIEW, PERMISSIONS.SQUAD_EDIT, PERMISSIONS.SQUAD_ADMIN],
  },
  {
    label: "Coaching",
    permissions: [PERMISSIONS.COACHING_VIEW, PERMISSIONS.COACHING_EDIT, PERMISSIONS.COACHING_ADMIN],
  },
  {
    label: "Scouting",
    permissions: [PERMISSIONS.SCOUTING_VIEW, PERMISSIONS.SCOUTING_EDIT, PERMISSIONS.SCOUTING_ADMIN],
  },
  {
    label: "Users",
    permissions: [PERMISSIONS.USERS_VIEW, PERMISSIONS.USERS_EDIT, PERMISSIONS.USERS_ADMIN],
  },
  {
    label: "Reports",
    permissions: [PERMISSIONS.REPORTS_VIEW, PERMISSIONS.REPORTS_EXPORT],
  },
];

/** All roles as array */
const ALL_ROLES = Object.values(ROLES);

/** System roles that cannot be renamed */
const SYSTEM_ROLES = new Set(ALL_ROLES);

/** Count users per role */
const userCountByRole = MOCK_USERS.reduce((acc, u) => {
  acc[u.role] = (acc[u.role] || 0) + 1;
  return acc;
}, {});

/** Color options for the role color select */
const COLOR_OPTIONS = ["info", "success", "warning", "error", "dark", "primary", "secondary"];

// ─── COMPONENT ─────────────────────────────────────────────────────────────────

function RolesPage() {
  const [expandedRole, setExpandedRole] = useState(null);
  const [matrixView, setMatrixView] = useState(false);

  // Local state for role data (supports Add/Delete/Edit without API)
  const [localRoles, setLocalRoles] = useState(ALL_ROLES);
  const [localRoleMeta, setLocalRoleMeta] = useState({ ...ROLE_META });
  const [localRolePerms, setLocalRolePerms] = useState({ ...ROLE_PERMISSIONS });

  // Dialog open state
  const [editRole, setEditRole] = useState(null);       // role string being edited, or null
  const [deleteRole, setDeleteRole] = useState(null);   // role string to delete, or null
  const [addRoleOpen, setAddRoleOpen] = useState(false);

  // Edit form state
  const [editForm, setEditForm] = useState({ name: "", description: "", color: "info", permissions: [] });

  // Add form state
  const [addForm, setAddForm] = useState({ name: "", description: "", permissions: [] });

  // ── Handlers: Edit ────────────────────────────────────────────────────────────

  const handleOpenEdit = (role) => {
    const meta = localRoleMeta[role] || {};
    setEditForm({
      name: role,
      description: meta.description || "",
      color: meta.color || "info",
      permissions: [...(localRolePerms[role] || [])],
    });
    setEditRole(role);
  };

  const handleEditPermToggle = (perm) => {
    setEditForm((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(perm)
        ? prev.permissions.filter((p) => p !== perm)
        : [...prev.permissions, perm],
    }));
  };

  const handleSaveEdit = () => {
    setLocalRoleMeta((prev) => ({
      ...prev,
      [editRole]: {
        ...(prev[editRole] || {}),
        description: editForm.description,
        color: editForm.color,
      },
    }));
    setLocalRolePerms((prev) => ({
      ...prev,
      [editRole]: editForm.permissions,
    }));
    setEditRole(null);
  };

  // ── Handlers: Delete ──────────────────────────────────────────────────────────

  const handleConfirmDelete = () => {
    setLocalRoles((prev) => prev.filter((r) => r !== deleteRole));
    setLocalRoleMeta((prev) => {
      const next = { ...prev };
      delete next[deleteRole];
      return next;
    });
    setLocalRolePerms((prev) => {
      const next = { ...prev };
      delete next[deleteRole];
      return next;
    });
    if (expandedRole === deleteRole) setExpandedRole(null);
    setDeleteRole(null);
  };

  // ── Handlers: Add ─────────────────────────────────────────────────────────────

  const handleOpenAdd = () => {
    setAddForm({ name: "", description: "", permissions: [] });
    setAddRoleOpen(true);
  };

  const handleAddPermToggle = (perm) => {
    setAddForm((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(perm)
        ? prev.permissions.filter((p) => p !== perm)
        : [...prev.permissions, perm],
    }));
  };

  const handleSaveAdd = () => {
    if (!addForm.name.trim()) return;
    const newRole = addForm.name.trim();
    setLocalRoles((prev) => [...prev, newRole]);
    setLocalRoleMeta((prev) => ({
      ...prev,
      [newRole]: { color: "info", icon: "person", description: addForm.description },
    }));
    setLocalRolePerms((prev) => ({
      ...prev,
      [newRole]: addForm.permissions,
    }));
    setAddRoleOpen(false);
  };

  // ── Matrix roles ──────────────────────────────────────────────────────────────

  const matrixRoles = localRoles.filter((r) => (localRolePerms[r] || []).length > 0);

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        {/* ── Page header ─────────────────────────────────────────────────── */}
        <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <SoftBox>
            <SoftTypography variant="h4" fontWeight="bold" color="dark">
              Role &amp; Permission Management
            </SoftTypography>
            <SoftTypography variant="body2" color="text">
              RBAC configuration — {localRoles.length} roles &middot; {Object.keys(PERMISSIONS).length} permissions
            </SoftTypography>
          </SoftBox>
          <SoftBox display="flex" gap={1}>
            <SoftButton
              variant="gradient"
              color="dark"
              onClick={handleOpenAdd}
            >
              <Icon sx={{ mr: 0.5 }}>add</Icon>
              Add Role
            </SoftButton>
            <SoftButton
              variant={matrixView ? "gradient" : "outlined"}
              color="info"
              onClick={() => setMatrixView((v) => !v)}
            >
              <Icon sx={{ mr: 0.5 }}>grid_view</Icon>
              {matrixView ? "Card View" : "Permission Matrix"}
            </SoftButton>
          </SoftBox>
        </SoftBox>

        {!matrixView ? (
          /* ── Role Cards Grid ─────────────────────────────────────────────── */
          <Grid container spacing={3}>
            {localRoles.map((role) => {
              const meta = localRoleMeta[role] || {};
              const perms = localRolePerms[role] || [];
              const userCount = userCountByRole[role] || 0;
              const isExpanded = expandedRole === role;

              return (
                <Grid key={role} item xs={12} sm={6} lg={4}>
                  <Card sx={{ height: "100%" }}>
                    <SoftBox p={3}>
                      {/* Role header */}
                      <SoftBox display="flex" alignItems="center" gap={1.5} mb={1}>
                        <SoftBox
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          width="2.5rem"
                          height="2.5rem"
                          borderRadius="lg"
                          sx={{ backgroundColor: "#f0f8ff" }}
                        >
                          <Icon sx={{ fontSize: "1.2rem", color: "#17c1e8" }}>{meta.icon || "person"}</Icon>
                        </SoftBox>
                        <SoftBox flex={1}>
                          <SoftTypography variant="button" fontWeight="bold" display="block">
                            {role}
                          </SoftTypography>
                          <SoftBox display="flex" gap={0.5} flexWrap="wrap" mt={0.25}>
                            <SoftBadge
                              badgeContent={`${userCount} user${userCount !== 1 ? "s" : ""}`}
                              color={meta.color || "info"}
                              size="xs"
                              container
                            />
                            <SoftBadge
                              badgeContent={`${perms.length} perms`}
                              color="secondary"
                              size="xs"
                              container
                            />
                          </SoftBox>
                        </SoftBox>
                      </SoftBox>

                      {/* Description */}
                      <SoftTypography variant="caption" color="text" display="block" mb={1.5}>
                        {meta.description}
                      </SoftTypography>

                      {/* View permissions toggle */}
                      <SoftButton
                        variant="outlined"
                        color="info"
                        size="small"
                        fullWidth
                        onClick={() => setExpandedRole(isExpanded ? null : role)}
                      >
                        <Icon sx={{ mr: 0.5, fontSize: "0.9rem" }}>
                          {isExpanded ? "expand_less" : "expand_more"}
                        </Icon>
                        {isExpanded ? "Hide Permissions" : "View Permissions"}
                      </SoftButton>

                      {/* Edit / Delete action buttons */}
                      <SoftBox display="flex" gap={1} mt={1}>
                        <SoftButton
                          variant="outlined"
                          color="info"
                          size="small"
                          fullWidth
                          onClick={() => handleOpenEdit(role)}
                        >
                          <Icon sx={{ mr: 0.5, fontSize: "0.9rem" }}>edit</Icon>
                          Edit Role
                        </SoftButton>
                        <SoftButton
                          variant="text"
                          color="error"
                          size="small"
                          fullWidth
                          onClick={() => setDeleteRole(role)}
                        >
                          <Icon sx={{ mr: 0.5, fontSize: "0.9rem" }}>delete</Icon>
                          Delete Role
                        </SoftButton>
                      </SoftBox>

                      {/* Permissions list — expanded */}
                      {isExpanded && (
                        <SoftBox mt={1.5}>
                          <Divider sx={{ mb: 1 }} />
                          {PERMISSION_GROUPS.map((group) => {
                            const groupPerms = group.permissions.filter((p) => perms.includes(p));
                            if (groupPerms.length === 0) return null;
                            return (
                              <SoftBox key={group.label} mb={1}>
                                <SoftTypography
                                  variant="caption"
                                  fontWeight="bold"
                                  color="text"
                                  textTransform="uppercase"
                                  display="block"
                                  mb={0.5}
                                >
                                  {group.label}
                                </SoftTypography>
                                {groupPerms.map((perm) => (
                                  <SoftBox
                                    key={perm}
                                    display="flex"
                                    alignItems="center"
                                    gap={0.5}
                                    mb={0.25}
                                  >
                                    <Icon sx={{ fontSize: "0.8rem", color: "#82d616" }}>check_circle</Icon>
                                    <SoftTypography variant="caption" color="text">
                                      {PERMISSION_LABELS[perm] || perm}
                                    </SoftTypography>
                                  </SoftBox>
                                ))}
                              </SoftBox>
                            );
                          })}
                        </SoftBox>
                      )}
                    </SoftBox>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        ) : (
          /* ── Permission Matrix ───────────────────────────────────────────── */
          <Card>
            <SoftBox p={3}>
              <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                <SoftTypography variant="h6" fontWeight="medium">
                  Full Permission Matrix
                </SoftTypography>
                <Tooltip title="Custom permissions — coming with API integration">
                  <span>
                    <SoftButton variant="outlined" color="info" size="small" disabled>
                      <Icon sx={{ mr: 0.5 }}>add</Icon>
                      Add Permission
                    </SoftButton>
                  </span>
                </Tooltip>
              </SoftBox>
              <SoftTypography variant="caption" color="text" display="block" mb={2}>
                Green check = permission granted · Dash = not granted
              </SoftTypography>

              <SoftBox sx={{ overflowX: "auto" }}>
                <SoftBox
                  component="table"
                  sx={{ width: "100%", borderCollapse: "collapse", fontSize: "0.7rem" }}
                >
                  {/* Header row with role names */}
                  <SoftBox component="thead">
                    <SoftBox component="tr">
                      {/* Permission label column */}
                      <SoftBox
                        component="th"
                        sx={{
                          textAlign: "left",
                          p: 1,
                          minWidth: 200,
                          fontWeight: 700,
                          fontSize: "0.65rem",
                          textTransform: "uppercase",
                          color: "text.secondary",
                          borderBottom: "2px solid #e9ecef",
                          position: "sticky",
                          left: 0,
                          backgroundColor: "#fff",
                          zIndex: 1,
                        }}
                      >
                        Permission
                      </SoftBox>
                      {matrixRoles.map((role) => {
                        const meta = localRoleMeta[role] || {};
                        return (
                          <Tooltip key={role} title={role} placement="top">
                            <SoftBox
                              component="th"
                              sx={{
                                textAlign: "center",
                                p: 1,
                                minWidth: 70,
                                fontWeight: 700,
                                fontSize: "0.6rem",
                                textTransform: "uppercase",
                                color: "text.secondary",
                                borderBottom: "2px solid #e9ecef",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                maxWidth: 80,
                              }}
                            >
                              <Icon sx={{ display: "block", mx: "auto", fontSize: "0.9rem", color: "#17c1e8" }}>
                                {meta.icon || "person"}
                              </Icon>
                              {/* Show abbreviated role name */}
                              {role.split(" ").map((w) => w[0]).join("")}
                            </SoftBox>
                          </Tooltip>
                        );
                      })}
                    </SoftBox>
                  </SoftBox>

                  {/* Permission rows grouped by module */}
                  <SoftBox component="tbody">
                    {PERMISSION_GROUPS.map((group) => (
                      <>
                        {/* Group header row */}
                        <SoftBox
                          key={`group-${group.label}`}
                          component="tr"
                          sx={{ backgroundColor: "#f8f9fa" }}
                        >
                          <SoftBox
                            component="td"
                            colSpan={matrixRoles.length + 1}
                            sx={{
                              p: 1,
                              fontWeight: 700,
                              fontSize: "0.65rem",
                              textTransform: "uppercase",
                              color: "#344767",
                              borderBottom: "1px solid #e9ecef",
                              position: "sticky",
                              left: 0,
                              backgroundColor: "#f8f9fa",
                            }}
                          >
                            {group.label}
                          </SoftBox>
                        </SoftBox>

                        {/* Individual permission rows */}
                        {group.permissions.map((perm, pi) => (
                          <SoftBox
                            key={perm}
                            component="tr"
                            sx={{
                              "&:hover": { backgroundColor: "#f8f9fa" },
                              backgroundColor: pi % 2 === 0 ? "white" : "#fdfdfd",
                            }}
                          >
                            {/* Permission label */}
                            <SoftBox
                              component="td"
                              sx={{
                                p: 1,
                                borderBottom: "1px solid #f0f0f0",
                                position: "sticky",
                                left: 0,
                                backgroundColor: "inherit",
                                zIndex: 1,
                              }}
                            >
                              <SoftTypography variant="caption" color="text">
                                {PERMISSION_LABELS[perm] || perm}
                              </SoftTypography>
                            </SoftBox>

                            {/* Check/dash for each role */}
                            {matrixRoles.map((role) => {
                              const hasIt = (localRolePerms[role] || []).includes(perm);
                              return (
                                <SoftBox
                                  key={role}
                                  component="td"
                                  sx={{
                                    textAlign: "center",
                                    p: 0.75,
                                    borderBottom: "1px solid #f0f0f0",
                                  }}
                                >
                                  {hasIt ? (
                                    <Icon
                                      sx={{ fontSize: "0.9rem", color: "#82d616" }}
                                      titleAccess="Granted"
                                    >
                                      check_circle
                                    </Icon>
                                  ) : (
                                    <SoftTypography
                                      variant="caption"
                                      sx={{ color: "#e0e0e0", fontSize: "0.8rem" }}
                                    >
                                      —
                                    </SoftTypography>
                                  )}
                                </SoftBox>
                              );
                            })}
                          </SoftBox>
                        ))}
                      </>
                    ))}
                  </SoftBox>
                </SoftBox>
              </SoftBox>

              {/* Legend */}
              <SoftBox display="flex" gap={3} mt={2} flexWrap="wrap">
                <SoftBox display="flex" alignItems="center" gap={0.5}>
                  <Icon sx={{ fontSize: "0.9rem", color: "#82d616" }}>check_circle</Icon>
                  <SoftTypography variant="caption" color="text">Permission granted</SoftTypography>
                </SoftBox>
                <SoftBox display="flex" alignItems="center" gap={0.5}>
                  <SoftTypography variant="caption" sx={{ color: "#e0e0e0", fontSize: "0.9rem" }}>—</SoftTypography>
                  <SoftTypography variant="caption" color="text">Not granted</SoftTypography>
                </SoftBox>
                <SoftTypography variant="caption" color="text">
                  Column headers show role initials — hover for full role name.
                </SoftTypography>
              </SoftBox>
            </SoftBox>
          </Card>
        )}
      </SoftBox>
      <Footer company={{ href: "#", name: "FC Metropolitan" }} links={[]} />

      {/* ── Edit Role Dialog ────────────────────────────────────────────────── */}
      <Dialog open={!!editRole} onClose={() => setEditRole(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Edit Role — {editRole}
        </DialogTitle>
        <DialogContent dividers>
          <SoftBox display="flex" flexDirection="column" gap={2} pt={1}>
            {/* TODO helper */}
            <Alert severity="info" sx={{ fontSize: "0.75rem" }}>
              TODO: Connect to API — changes are local only and will reset on page reload.
            </Alert>

            {/* Role name */}
            <TextField
              label="Role Name"
              value={editForm.name}
              InputProps={{ readOnly: SYSTEM_ROLES.has(editRole) }}
              InputLabelProps={{ shrink: true }}
              helperText={SYSTEM_ROLES.has(editRole) ? "System roles cannot be renamed" : ""}
              fullWidth
              size="small"
            />

            {/* Role color */}
            <TextField
              label="Role Color"
              value={editForm.color}
              onChange={(e) => setEditForm((prev) => ({ ...prev, color: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              select
              SelectProps={{ native: true }}
              fullWidth
              size="small"
            >
              {COLOR_OPTIONS.map((c) => (
                <option key={c} value={c}>
                  {c.charAt(0).toUpperCase() + c.slice(1)}
                </option>
              ))}
            </TextField>

            {/* Description */}
            <TextField
              label="Description"
              value={editForm.description}
              onChange={(e) => setEditForm((prev) => ({ ...prev, description: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              multiline
              minRows={2}
              fullWidth
              size="small"
            />

            {/* Permissions */}
            <SoftBox>
              <SoftTypography variant="caption" fontWeight="bold" color="text" textTransform="uppercase" display="block" mb={1}>
                Permissions
              </SoftTypography>
              {PERMISSION_GROUPS.map((group) => (
                <SoftBox key={group.label} mb={1.5}>
                  <SoftTypography variant="caption" fontWeight="bold" color="text" display="block" mb={0.5}>
                    {group.label}
                  </SoftTypography>
                  <FormGroup>
                    {group.permissions.map((perm) => (
                      <FormControlLabel
                        key={perm}
                        control={
                          <Checkbox
                            checked={editForm.permissions.includes(perm)}
                            onChange={() => handleEditPermToggle(perm)}
                            size="small"
                          />
                        }
                        label={
                          <SoftTypography variant="caption" color="text">
                            {PERMISSION_LABELS[perm] || perm}
                          </SoftTypography>
                        }
                      />
                    ))}
                  </FormGroup>
                  <Divider />
                </SoftBox>
              ))}
            </SoftBox>
          </SoftBox>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <SoftButton variant="outlined" color="secondary" onClick={() => setEditRole(null)}>
            Cancel
          </SoftButton>
          <SoftButton variant="gradient" color="info" onClick={handleSaveEdit}>
            Save Changes
          </SoftButton>
        </DialogActions>
      </Dialog>

      {/* ── Add Role Dialog ─────────────────────────────────────────────────── */}
      <Dialog open={addRoleOpen} onClose={() => setAddRoleOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Role</DialogTitle>
        <DialogContent dividers>
          <SoftBox display="flex" flexDirection="column" gap={2} pt={1}>
            {/* TODO helper */}
            <Alert severity="info" sx={{ fontSize: "0.75rem" }}>
              TODO: Connect to API — new roles are local only and will reset on page reload.
            </Alert>

            {/* Role name */}
            <TextField
              label="Role Name"
              value={addForm.name}
              onChange={(e) => setAddForm((prev) => ({ ...prev, name: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              required
              fullWidth
              size="small"
              placeholder="e.g. Data Analyst"
            />

            {/* Description */}
            <TextField
              label="Description"
              value={addForm.description}
              onChange={(e) => setAddForm((prev) => ({ ...prev, description: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              multiline
              minRows={2}
              fullWidth
              size="small"
              placeholder="Brief description of this role's responsibilities"
            />

            {/* Permissions */}
            <SoftBox>
              <SoftTypography variant="caption" fontWeight="bold" color="text" textTransform="uppercase" display="block" mb={1}>
                Permissions
              </SoftTypography>
              {PERMISSION_GROUPS.map((group) => (
                <SoftBox key={group.label} mb={1.5}>
                  <SoftTypography variant="caption" fontWeight="bold" color="text" display="block" mb={0.5}>
                    {group.label}
                  </SoftTypography>
                  <FormGroup>
                    {group.permissions.map((perm) => (
                      <FormControlLabel
                        key={perm}
                        control={
                          <Checkbox
                            checked={addForm.permissions.includes(perm)}
                            onChange={() => handleAddPermToggle(perm)}
                            size="small"
                          />
                        }
                        label={
                          <SoftTypography variant="caption" color="text">
                            {PERMISSION_LABELS[perm] || perm}
                          </SoftTypography>
                        }
                      />
                    ))}
                  </FormGroup>
                  <Divider />
                </SoftBox>
              ))}
            </SoftBox>
          </SoftBox>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <SoftButton variant="outlined" color="secondary" onClick={() => setAddRoleOpen(false)}>
            Cancel
          </SoftButton>
          <SoftButton
            variant="gradient"
            color="dark"
            onClick={handleSaveAdd}
            disabled={!addForm.name.trim()}
          >
            <Icon sx={{ mr: 0.5 }}>add</Icon>
            Create Role
          </SoftButton>
        </DialogActions>
      </Dialog>

      {/* ── Delete Confirmation Dialog ──────────────────────────────────────── */}
      <Dialog open={!!deleteRole} onClose={() => setDeleteRole(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Delete Role</DialogTitle>
        <DialogContent dividers>
          <SoftBox display="flex" flexDirection="column" gap={2} pt={1}>
            <SoftTypography variant="body2" color="text">
              Are you sure you want to delete <strong>{deleteRole}</strong>?
            </SoftTypography>
            {deleteRole && (userCountByRole[deleteRole] || 0) > 0 && (
              <Alert severity="warning" sx={{ fontSize: "0.75rem" }}>
                Warning: {userCountByRole[deleteRole]} user{userCountByRole[deleteRole] !== 1 ? "s" : ""} have this role. They will lose access.
              </Alert>
            )}
            <Alert severity="info" sx={{ fontSize: "0.75rem" }}>
              TODO: Connect to API — deletion is local only and will reset on page reload.
            </Alert>
          </SoftBox>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <SoftButton variant="outlined" color="secondary" onClick={() => setDeleteRole(null)}>
            Cancel
          </SoftButton>
          <SoftButton variant="gradient" color="error" onClick={handleConfirmDelete}>
            <Icon sx={{ mr: 0.5 }}>delete</Icon>
            Delete
          </SoftButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default RolesPage;
